import React from "react";
import Layout from "../Layout";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import UAEDirhamIcon from "../../utils/UAEDirhamIcon";

function PricingPage() {
  const { t } = useTranslation();

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const cardItem = {
    hidden: { opacity: 0, y: 40 },
    show: { 
      opacity: 1, 
      y: 0, 
      transition: { duration: 0.6 } 
    },
  };

  return (
    <Layout title={t("Pricing.title")}>
      {/* Hero Section */}
      <section className="py-8 md:py-12 lg:py-16 relative overflow-hidden">
        {/* خلفية متدرجة */}
        <div className="absolute inset-0 bg-gradient-to-br from-MainBlue/5 via-blue-600/3 to-MainBlue/5"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-MainBlue/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-600/5 rounded-full blur-3xl"></div>

        <div className="relative max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-10 md:mb-14"
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4">
              <span className="bg-gradient-to-r from-MainBlue via-blue-600 to-MainBlue bg-clip-text text-transparent">
                {t("Pricing.subscriptionPlans")}
              </span>
            </h1>
            <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
              {t("Pricing.subscriptionPlans")}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Pricing Cards Section */}
      <section className="py-8 md:py-12 lg:py-16 bg-gradient-to-br from-muted via-muted/50 to-muted/30">
        <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-8">
          <motion.div
            variants={container}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.1 }}
            className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8"
          >
            {/* Monthly Plan */}
            <motion.div
              variants={cardItem}
              whileHover={{ y: -8, scale: 1.02 }}
              className="relative rounded-xl md:rounded-2xl border-2 border-gray-200 dark:border-gray-700 p-6 md:p-8 flex flex-col shadow-md hover:shadow-xl transition-all duration-300 bg-card backdrop-blur-sm overflow-hidden group h-full"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-MainBlue/5 rounded-full blur-2xl group-hover:bg-MainBlue/10 transition-colors"></div>
              
              <div className="relative z-10 flex flex-col h-full space-y-4 md:space-y-6">
                <div className="flex-shrink-0">
                  <h3 className="text-xl md:text-2xl font-bold text-foreground">
                    {t("Pricing.monthlyPlanTitle")}
                  </h3>
                  
                  <div className="space-y-2 mt-4">
                    <p className="text-sm md:text-base text-gray-500 line-through">
                      <span dir="ltr">
                        <UAEDirhamIcon className="w-4 h-4 inline-block" /> 119
                      </span> /{t("Pricing.perMonth")}
                    </p>
                    <div className="flex items-baseline gap-2">
                      <UAEDirhamIcon className="w-6 h-6 md:w-8 md:h-8 text-MainBlue" />
                      <span className="text-4xl md:text-5xl lg:text-6xl font-bold text-MainBlue">95</span>
                      <span className="text-base md:text-lg font-medium text-muted-foreground">
                        /{t("Pricing.perMonth")}
                      </span>
                    </div>
                  </div>
                </div>

                <ul className="flex-1 space-y-3 text-sm md:text-base">
                  {[0, 1, 3, 4, 5].map((index) => (
                    <li key={index} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-MainBlue flex-shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">{t(`Pricing.monthlyFeatures.${index}`)}</span>
                    </li>
                  ))}
                </ul>

                <motion.a
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  href="https://wa.me/971528401112?text=Hello%20👋%2C%20I%20am%20interested%20in%20the%20Monthly%20Plan%20on%20TWHMENU.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-shrink-0 mt-auto w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white py-3 px-6 rounded-lg font-semibold text-center shadow-md hover:shadow-lg transition-all"
                >
                  {t("Pricing.chooseMonthly")}
                </motion.a>
              </div>
            </motion.div>

            {/* Yearly Plan - المميزة */}
            <motion.div
              variants={cardItem}
              whileHover={{ y: -8, scale: 1.02 }}
              className="relative rounded-xl md:rounded-2xl p-6 md:p-8 flex flex-col shadow-xl transition-all duration-300 bg-gradient-to-br from-MainBlue/10 via-blue-600/5 to-MainBlue/10 border-2 border-MainBlue overflow-visible group h-full"
            >
              {/* شارة الأفضل - محسّنة */}
              <motion.div
                initial={{ rotate: -12 }}
                whileHover={{ rotate: 0, scale: 1.05 }}
                className="absolute -top-3 -right-2 md:-top-4 md:-right-3 z-30"
              >
                <div className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white text-xs md:text-sm font-bold px-3 md:px-4 py-1.5 md:py-2 rounded-full shadow-lg whitespace-nowrap">
                  {t("Pricing.bestValue")}
                </div>
              </motion.div>

              <div className="absolute inset-0 bg-gradient-to-br from-MainBlue/10 to-blue-600/10 rounded-xl md:rounded-2xl"></div>
              <div className="absolute top-0 right-0 w-40 h-40 bg-MainBlue/10 rounded-full blur-3xl group-hover:bg-MainBlue/20 transition-colors"></div>
              
              <div className="relative z-10 flex flex-col h-full space-y-4 md:space-y-6 pt-2">
                <div className="flex-shrink-0 text-center">
                  <h3 className="text-xl md:text-2xl font-bold text-foreground">
                    {t("Pricing.yearlyPlanTitle")}
                  </h3>

                  <div className="space-y-2 mt-4">
                    {/* السعر الشهري */}
                    <p className="text-base md:text-lg text-muted-foreground">
                      <span dir="ltr">
                        <UAEDirhamIcon className="w-4 h-4 inline-block" /> 65
                      </span> /{t("Pricing.perMonth")}
                    </p>

                    {/* السعر الكامل قبل الخصم */}
                    <p className="text-sm md:text-base text-gray-500 line-through">
                      <span dir="ltr">
                        <UAEDirhamIcon className="w-4 h-4 inline-block" /> 1140
                      </span> /{t("Pricing.perYear")}
                    </p>

                    {/* الخصم */}
                    <motion.div
                      initial={{ scale: 0 }}
                      whileInView={{ scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ type: "spring", delay: 0.3 }}
                      className="inline-block"
                    >
                      <span className="text-sm md:text-base font-bold text-green-600 bg-green-50 dark:bg-green-900/20 px-3 py-1 rounded-full">
                        {t("Pricing.save45")}
                      </span>
                    </motion.div>

                    {/* السعر النهائي */}
                    <div className="flex items-baseline justify-center gap-2 pt-2">
                      <UAEDirhamIcon className="w-6 h-6 md:w-8 md:h-8 text-MainBlue" />
                      <span className="text-4xl md:text-5xl lg:text-6xl font-bold text-MainBlue">780</span>
                      <span className="text-base md:text-lg font-medium text-muted-foreground">
                        /{t("Pricing.perYear")}
                      </span>
                    </div>
                  </div>
                </div>

                <ul className="flex-1 space-y-3 text-sm md:text-base text-left">
                  {[0, 1, 2, 3, 4, 6].map((index) => (
                    <li key={index} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-MainBlue flex-shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">{t(`Pricing.yearlyFeatures.${index}`)}</span>
                    </li>
                  ))}
                </ul>

                <motion.a
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  href="https://wa.me/971528401112?text=Hello%20👋%2C%20I%20am%20interested%20in%20the%20Yearly%20Plan%20on%20TWHMENU.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-shrink-0 mt-auto w-full bg-gradient-to-r from-MainBlue to-blue-600 text-white py-3 px-6 rounded-lg font-semibold text-center shadow-lg hover:shadow-xl transition-all"
                >
                  {t("Pricing.goYearly")}
                </motion.a>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}

export default PricingPage;
